import { AppstoreOutlined, CustomerServiceOutlined, StopOutlined, UserOutlined } from '@ant-design/icons'
import { Menu } from 'antd'
import { AppRoutes } from 'app/app-routes'
import { FC, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { generateSpaceSettingsMenu } from '../menu-items'
import { useGetSpacesQuery } from 'api/spaces-api'
import { useGetWorkspacesOptionsQuery } from 'api/workspaces-api'
import { Pto } from '@outcoreaix/pto'
import Icon from '@ant-design/icons/lib/components/Icon'

export const getSpaceOptions = (space: Pto.Spaces.SpaceListItem, navigate: (path: string) => void) => {
  const role = space?.currentSpaceMember?.role
  const hasSpaceSettingsSubmenu = role === Pto.Spaces.SpaceMemberRole.Admin || role === Pto.Spaces.SpaceMemberRole.Staff

  return {
    key: hasSpaceSettingsSubmenu ? `space_${space.id}` : `/settings/${space.workspaceId}/space/${space.id}/members`,

    ...(!hasSpaceSettingsSubmenu && {
      onClick: () => navigate(`/settings/${space.workspaceId}/space/${space.id}/members`)
    }),

    label: (
      <div className="pl-1 overflow-hidden text-ellipsis whitespace-nowrap max-w-[165px]">
        {space.status === Pto.Spaces.SpaceStatus.Inactive && <StopOutlined className="text-red-500 mr-1" />}
        {space.name}
      </div>
    ),

    ...(hasSpaceSettingsSubmenu && {
      children: generateSpaceSettingsMenu(space.workspaceId, space.id, role)
    })
  }
}

const SettingsMenu: FC = () => {
  const param = useParams()
  const { data: workspaces } = useGetWorkspacesOptionsQuery()
  const currentWorkspace = param.workspaceId
    ? workspaces?.find((item) => item.id === Number(param.workspaceId))
    : undefined

  const { data } = useGetSpacesQuery(
    {
      workspaceId: Number(param.workspaceId)
    },
    { skip: param.workspaceId === undefined }
  )
  // TODO: add loading state
  const spaces = useMemo(
    () => data?.items.filter((item) => item.workspaceId === Number(currentWorkspace?.id)),
    [currentWorkspace, data]
  )
  const navigate = useNavigate()
  const currentRole = workspaces?.find((item) => item.id === Number(param.workspaceId))?.currentMember?.role

  return (
    <div className="flex flex-col h-screen overflow-hidden w-full">
      <Menu
        theme="light"
        mode="inline"
        id="settings"
        className="h-full py-6 px-4 overflow-y-auto border-r border-menu"
        triggerSubMenuAction="click"
        defaultOpenKeys={[`space_${param.spaceId}`, `workspace_${param.workspaceId}`]}
        defaultSelectedKeys={[location.pathname]}
        items={[
          {
            key: `${AppRoutes.settings}/${currentWorkspace?.id}/profile-settings`,
            icon: <UserOutlined />,
            label: <Link to={`${AppRoutes.settings}/${currentWorkspace?.id}/profile-settings`}>Profile settings</Link>,
            className: '!pl-3'
          },
          {
            key: `${AppRoutes.settings}/${currentWorkspace?.id}/support`,
            icon: <CustomerServiceOutlined />,
            label: <Link to={`${AppRoutes.settings}/${currentWorkspace?.id}/support`}>Support</Link>,
            className: '!pl-3'
          },
          ...(currentRole !== Pto.Workspaces.WorkspaceRole.Contact
            ? [
                ...(currentRole === Pto.Workspaces.WorkspaceRole.Admin
                  ? [
                      {
                        key: `${AppRoutes.settings}/${currentWorkspace?.id}/general`,
                        icon: <AppstoreOutlined />,
                        label: <Link to={`/settings/${currentWorkspace?.id}/general`}>College Settings</Link>,
                        className: '!pl-3'
                      }
                    ]
                  : []),
                {
                  key: 'spaces',
                  type: 'group' as const,
                  label: (
                    <div className="flex gap-2 items-center">
                      <Icon
                        className="w-4 h-4"
                        component={() => (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22" fill="currentColor">
                            <path d="m11 1 .447-.894a1 1 0 0 0-.894 0L11 1ZM1 6l-.447-.894a1 1 0 0 0 0 1.788L1 6Zm10 5-.447.894a1 1 0 0 0 .894 0L11 11Zm10-5 .447.894a1 1 0 0 0 0-1.788L21 6ZM1.447 15.106a1 1 0 1 0-.894 1.788l.894-1.788ZM11 21l-.447.894a1 1 0 0 0 .894 0L11 21Zm10.447-4.106a1 1 0 1 0-.894-1.788l.894 1.788Zm-20-6.788a1 1 0 1 0-.894 1.788l.894-1.788ZM11 16l-.447.894a1 1 0 0 0 .894 0L11 16Zm10.447-4.106a1 1 0 1 0-.894-1.788l.894 1.788ZM10.553.106l-10 5 .894 1.788 10-5-.894-1.788Zm-10 6.788 10 5 .894-1.788-10-5-.894 1.788Zm10.894 5 10-5-.894-1.788-10 5 .894 1.788Zm10-6.788-10-5-.894 1.788 10 5 .894-1.788ZM.553 16.894l10 5 .894-1.788-10-5-.894 1.788Zm10.894 5 10-5-.894-1.788-10 5 .894 1.788Zm-10.894-10 10 5 .894-1.788-10-5-.894 1.788Zm10.894 5 10-5-.894-1.788-10 5 .894 1.788Z" />
                          </svg>
                        )}
                      />
                      Spaces
                    </div>
                  ),
                  children: [...(spaces || []).filter((s) => !!s).map((space) => getSpaceOptions(space, navigate))]
                }
              ]
            : [])
        ]}
      />
    </div>
  )
}

export default SettingsMenu
